import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../Context/UserContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaBuilding,
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowLeft,
  FaSearch,
  FaSave,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaCog,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaCogs,
  FaExclamationTriangle,
  FaUsers
} from 'react-icons/fa';
import CustomerCustomizationModal from './CustomerCustomizationModal';
import './CreateCustomerPage.css';

/**
 * TaxMaster — SaaS Production Version
 *
 * DB schema per record:
 *   Company-level: { _id, tenant_id, company_id, company_name, tax_name, tax_type, tax_rate }
 *   Branch-level:  { _id, tenant_id, company_id, company_name, branch_id, branch_name, tax_name, ... }
 */
const TaxMaster = ({ isModal = false, onClose = () => {} }) => {
  const navigate  = useNavigate();
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);

  // ─── Data ────────────────────────────────────────────────────────────────────
  const [taxes,          setTaxes]          = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [branchesMap,    setBranchesMap]    = useState({});   // { "CompanyName": ["Branch1","Branch2"] }
  const [availableBranches, setAvailableBranches] = useState([]);

  // ─── Auth / Permissions ───────────────────────────────────────────────────────
  const [permissions,    setPermissions]    = useState({ canWrite: false, canDelete: false });
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin,   setIsGroupAdmin]   = useState(false);

  // ─── UI ───────────────────────────────────────────────────────────────────────
  const [loading,          setLoading]          = useState(true);
  const [error,            setError]            = useState('');
  const [successMessage,   setSuccessMessage]   = useState('');
  const [showForm,         setShowForm]         = useState(isModal);
  const [searchQuery,      setSearchQuery]      = useState('');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showDeleteConfirm,  setShowDeleteConfirm]  = useState(false);
  const [deleteId,           setDeleteId]           = useState(null);

  // ─── Form ─────────────────────────────────────────────────────────────────────
  const [doctypeFields,    setDoctypeFields]   = useState([]);
  const [formData,         setFormData]        = useState({ tax_name: '', tax_type: 'VAT', tax_rate: '', is_active: true });
  const [editingId,        setEditingId]       = useState(null);

  // Multi-tenant selection in form
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedBranches,  setSelectedBranches]  = useState([]);  // branch names chosen in form
  const [formBranches,      setFormBranches]      = useState([]);   // branches available for selected companies

  // ─── Filters (list view) ──────────────────────────────────────────────────────
  const [selectedViewCompany,   setSelectedViewCompany]   = useState('');
  const [selectedBranchFilter,  setSelectedBranchFilter]  = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  //  INIT
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (configLoading) return;
    const url = baseUrl || '';
    const init = async () => {
      setLoading(true);
      setError('');
      try {
        await Promise.all([
          fetchPermissions(url),
          fetchTaxes(url),
          fetchMetadata(url),
          fetchAllBranches(url),
        ]);
      } catch {
        setError('Failed to load tax data.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [baseUrl, configLoading]);

  // When selected companies change → refresh form branches
  useEffect(() => {
    const branches = [];
    selectedCompanies.forEach(comp => {
      const b = branchesMap[comp] || [];
      b.forEach(br => { if (!branches.includes(br)) branches.push(br); });
    });
    setFormBranches(branches);
    // Remove selected branches that no longer belong to chosen companies
    setSelectedBranches(prev => prev.filter(b => branches.includes(b)));
  }, [selectedCompanies, branchesMap]);

  // Auto-clear feedback
  useEffect(() => {
    if (!error && !successMessage) return;
    const t = setTimeout(() => { setError(''); setSuccessMessage(''); }, 4500);
    return () => clearTimeout(t);
  }, [error, successMessage]);

  // ─────────────────────────────────────────────────────────────────────────────
  //  DATA FETCHERS
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchTaxes = async (url) => {
    try {
      const res = await axios.get(`${url}/api/taxes`, { headers: getHeaders() });
      const raw  = res.data || [];
      // Deduplicate by global_ref_id so each logical tax shows once
      const seen = new Set();
      const out  = [];
      for (const t of raw) {
        const key = t.global_ref_id || t._id || t.id;
        if (!seen.has(key)) { seen.add(key); out.push(t); }
      }
      setTaxes(out);
    } catch (err) {
      console.error('Fetch Taxes Error:', err);
    }
  };

  const fetchMetadata = async (url) => {
    try {
      const res = await axios.get(
        `${url}/api/doctypes/${encodeURIComponent('Tax Master')}`,
        { headers: getHeaders() }
      );
      const fields = res.data.fields || [];
      setDoctypeFields(fields);
    } catch {
      setDoctypeFields([
        { id: 'tax_name',  label: 'Tax Name',    type: 'Data',   mandatory: true },
        { id: 'tax_type',  label: 'Tax Type',     type: 'Select', mandatory: true,
          options: 'VAT\nExcise Duty\nGST\nService Charge\nOther' },
        { id: 'tax_rate',  label: 'Tax Rate (%)', type: 'Float',  mandatory: true },
        { id: 'is_active', label: 'Is Active',    type: 'Check'  }
      ]);
    }
  };

  const fetchPermissions = async (url) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user    = JSON.parse(userStr);
      const roleRaw = (user.role || user.UserType || '').toLowerCase().replace(' ', '_');
      const branch  = user.branch_name || user.branch || '';

      const isGroup = ['group_admin','groupadmin','tenant_admin','superadmin'].includes(roleRaw);
      const isAdmin = ['company_admin','admin','group_admin','groupadmin','tenant_admin','superadmin'].includes(roleRaw) &&
        (!branch || branch === 'All Branches' || branch === '');

      setIsGroupAdmin(isGroup);
      setIsCompanyAdmin(isAdmin);

      // Build companyOptions
      let comps = [];
      if (user.companies && user.companies.length > 0) {
        comps = user.companies.map(c =>
          typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c
        ).filter(Boolean);
      } else if (user.company_name || user.company) {
        comps = [user.company_name || user.company];
      } else {
        try {
          const res = await axios.get(`${url}/api/company-details`, { headers: getHeaders() });
          comps = (res.data.companyDetails || []).map(d => d.restaurantName || d.company_name).filter(Boolean);
        } catch { /* ignore */ }
      }
      const uniqueComps = [...new Set(comps)];
      setCompanyOptions(uniqueComps);

      // Pre-select company
      const activeCtx = localStorage.getItem('active_company');
      if (activeCtx && activeCtx !== 'All' && activeCtx !== 'All Companies') {
        setSelectedCompanies([activeCtx]);
      } else {
        setSelectedCompanies(uniqueComps.length === 1 ? [uniqueComps[0]] : []);
      }

      // Permissions
      if (['company_admin','admin','branch_admin','group_admin','tenant_admin','master_admin','owner','superadmin'].includes(roleRaw)) {
        setPermissions({ canWrite: true, canDelete: true });
      } else {
        try {
          const res = await axios.get(
            `${url}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`,
            { headers: getHeaders() }
          );
          const pagePerm = (res.data.permissions || []).find(p =>
            ['tax_master','purchase','admin'].includes(p.pageId)
          );
          if (pagePerm) setPermissions({ canWrite: !!pagePerm.canWrite, canDelete: !!pagePerm.canDelete });
        } catch { /* ignore */ }
      }
    } catch (err) {
      console.warn('Permissions error:', err);
    }
  };

  const fetchAllBranches = async (url) => {
    try {
      const userStr   = localStorage.getItem('user');
      const userObj   = userStr ? JSON.parse(userStr) : {};
      const activeCtx = localStorage.getItem('active_company');

      let compsToFetch = [];
      if (activeCtx && activeCtx !== 'All' && activeCtx !== 'All Companies') {
        compsToFetch = [activeCtx];
      } else if (userObj.companies && userObj.companies.length > 0) {
        compsToFetch = userObj.companies.map(c =>
          typeof c === 'object' ? (c.company_name || c.company || '') : c
        ).filter(Boolean);
      } else if (userObj.company_name || userObj.company) {
        compsToFetch = [userObj.company_name || userObj.company];
      }

      if (!compsToFetch.length) return;

      const map      = {};
      const allNames = [];

      await Promise.all(compsToFetch.map(async comp => {
        try {
          const res = await axios.get(
            `${url}/api/branches?company_name=${encodeURIComponent(comp)}`,
            { headers: { ...getHeaders(), 'X-Company-Name': comp } }
          );
          const names = (res.data || []).map(b =>
            typeof b === 'string' ? b : (b.branch_name || b.name || '')
          ).filter(Boolean);
          map[comp] = names;
          names.forEach(n => { if (!allNames.includes(n)) allNames.push(n); });
        } catch (e) { console.warn('Branch fetch error for', comp, e); }
      }));

      setBranchesMap(map);
      setAvailableBranches(allNames);
    } catch (err) {
      console.error('Fetch branches error:', err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  //  FORM HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleCompany = (comp) => {
    setSelectedCompanies(prev =>
      prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]
    );
  };

  const toggleBranch = (brName) => {
    setSelectedBranches(prev =>
      prev.includes(brName) ? prev.filter(b => b !== brName) : [...prev, brName]
    );
  };

  const resetForm = () => {
    setFormData({ tax_name: '', tax_type: 'VAT', tax_rate: '', is_active: true });
    setEditingId(null);
    setSelectedBranches([]);
    const activeCtx = localStorage.getItem('active_company');
    if (activeCtx && activeCtx !== 'All' && activeCtx !== 'All Companies') {
      setSelectedCompanies([activeCtx]);
    } else {
      setSelectedCompanies(companyOptions.length === 1 ? [companyOptions[0]] : []);
    }
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!permissions.canWrite) {
      setError('Permission Denied: You cannot create or edit taxes.');
      return;
    }

    // Validate mandatory fields
    const missing = doctypeFields.filter(
      f => f.mandatory && !f.hidden && (formData[f.id] === undefined || formData[f.id] === '')
    );
    if (missing.length) {
      setError(`Required: ${missing.map(f => f.label).join(', ')}`);
      setTimeout(() => setError(''), 4500);
      return;
    }

    setShowAssignmentModal(true);
  };

  const handleSubmit = async () => {
    // Determine companies to save
    const activeCtx  = localStorage.getItem('active_company');
    const userStr    = localStorage.getItem('user');
    const userData   = userStr ? JSON.parse(userStr) : {};
    const userComp   = userData.company_name || userData.company || '';
    const companiesToProcess =
      activeCtx === 'All' || activeCtx === 'All Companies'
        ? selectedCompanies
        : [activeCtx || userComp || ''].filter(Boolean);

    if (!companiesToProcess.length) {
      setError('Please select at least one company.');
      return;
    }

    // Determine branches
    const activeBranchLocal = localStorage.getItem('active_branch');
    const isSpecificBranch  = activeBranchLocal &&
      activeBranchLocal !== 'All Branches' &&
      activeBranchLocal !== 'All' &&
      activeBranchLocal !== '';
    const branchesToProcess = isSpecificBranch ? [activeBranchLocal] : selectedBranches;

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const url     = baseUrl || '';
      const headers = getHeaders();
      if (companiesToProcess[0]) headers['X-Company-Name'] = companiesToProcess[0];
      if (branchesToProcess[0])  headers['X-Branch-Name']  = branchesToProcess[0];

      const payload = {
        ...formData,
        tax_name:     (formData.tax_name || '').trim(),
        tax_rate:     parseFloat(formData.tax_rate) || 0,
        company_names: companiesToProcess,
        company_name:  companiesToProcess[0] || '',
        branch_names:  branchesToProcess,
        branch_name:   branchesToProcess[0] || ''
      };

      if (editingId) {
        await axios.put(`${url}/api/taxes/${editingId}`, payload, { headers });
        setSuccessMessage('Tax updated successfully!');
      } else {
        await axios.post(`${url}/api/taxes`, payload, { headers });
        setSuccessMessage('Tax created successfully!');
      }

      await fetchTaxes(url);
      setShowAssignmentModal(false);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save tax.');
    } finally {
      setLoading(false);
    }
  };

  const initiateEdit = (tax) => {
    setEditingId(tax._id || tax.id);
    setFormData({
      tax_name:  tax.tax_name  || '',
      tax_type:  tax.tax_type  || 'VAT',
      tax_rate:  tax.tax_rate  !== undefined ? String(tax.tax_rate) : '',
      is_active: tax.is_active !== false,
      ...Object.fromEntries(
        Object.entries(tax).filter(([k]) =>
          !['_id','id','company_name','company','company_names','branch_name','branch','branch_names',
            'tenant_id','company_id','branch_id','global_ref_id','created_at','updated_at'].includes(k)
        )
      )
    });

    const comps = new Set();
    const brs   = new Set();
    if (Array.isArray(tax.company_names)) tax.company_names.forEach(c => comps.add(c));
    else if (tax.company_name) comps.add(tax.company_name);
    else if (tax.company)      comps.add(tax.company);

    if (Array.isArray(tax.branch_names))   tax.branch_names.forEach(b => { if (b && b !== 'All Branches') brs.add(b); });
    else if (typeof tax.branch_names === 'string') tax.branch_names.split(',').forEach(b => { if (b.trim() && b.trim() !== 'All Branches') brs.add(b.trim()); });
    else if (tax.branch_name && tax.branch_name !== 'All Branches') brs.add(tax.branch_name);
    else if (tax.branch      && tax.branch      !== 'All Branches') brs.add(tax.branch);

    setSelectedCompanies(Array.from(comps));
    setSelectedBranches(Array.from(brs));
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const executeDelete = async () => {
    if (!permissions.canDelete) {
      setError('Permission Denied: You cannot delete.');
      setShowDeleteConfirm(false);
      return;
    }
    try {
      await axios.delete(`${baseUrl || ''}/api/taxes/${deleteId}`, { headers: getHeaders() });
      setSuccessMessage('Tax deleted successfully.');
      setTaxes(prev => prev.filter(t => (t._id || t.id) !== deleteId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete tax.');
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const filteredTaxes = taxes.filter(t => {
    const q = (searchQuery || '').toLowerCase();
    const matchSearch  = !q
      || (t.tax_name || '').toLowerCase().includes(q)
      || (t.tax_type || '').toLowerCase().includes(q);
    const matchComp   = !selectedViewCompany  || (t.company_name || t.company)  === selectedViewCompany;
    const matchBranch = !selectedBranchFilter || (t.branch_name  || t.branch)   === selectedBranchFilter;
    return matchSearch && matchComp && matchBranch;
  });

  if (loading && !taxes.length && !showForm) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)'
      }}>
        <div style={{ textAlign: 'center', color: '#3498db', fontSize: '18px' }}>
          <FaMoneyBillWave style={{ fontSize: '48px', marginBottom: '20px', color: '#3498db' }} />
          <p>Loading Tax Master...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-customer-container main-customer-page">
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={() => isModal ? onClose() : (showForm ? (setShowForm(false), resetForm()) : navigate('/admin'))} className="header-back-btn" disabled={loading}>
            {isModal ? <><FaTimes /> Close</> : <><FaArrowLeft /> Back to Admin</>}
          </button>
        </div>

        <div className="header-center">
          <h1>Tax Master</h1>
        </div>

        <div className="header-right">
          <div className="header-actions">
            <button 
              type="button"
              className="customize-btn" 
              onClick={(e) => { e.preventDefault(); setShowCustomizeModal(true); }}
              style={{
                background: '#604BE8', color: '#ffffff', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '48px', minWidth: '140px', borderRadius: '14px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 6px -1px rgba(96, 75, 232, 0.2)'
              }}
            >
              <FaCogs /> Customize
            </button>
            {!showForm && permissions.canWrite && (
              <button className="save-btn" onClick={() => { resetForm(); setShowForm(true); window.scrollTo(0, 0); }} disabled={loading}>
                <FaPlus /> Add New
              </button>
            )}
            {showForm && (
               <button className="save-btn" onClick={handlePreSubmit} disabled={loading} style={{ background: '#3b82f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                 <FaSave /> {loading ? 'Processing...' : 'Save Tax'}
               </button>
            )}
          </div>
        </div>
      </div>
      <div className="header-divider"></div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ width: '100%', maxWidth: '1000px', margin: '0 auto' }}>

        {showForm ? (
          <form style={{ marginBottom: '30px' }}>
            <div className="form-section-card theme-blue">
              <div className="section-header">
                <div className="section-icon"><FaMoneyBillWave /></div>
                <h2>{editingId ? 'Edit Tax' : 'Add Tax'}</h2>
              </div>
              <div className="form-grid" style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '20px', alignItems: 'end' }}>
                  {doctypeFields.filter(f => !f.hidden).map(field => {
                    const type = (field.type || '').toLowerCase();
                    const isDisabled = field.disabled || field.read_only;
                    return (
                      <div key={field.id} style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', color: '#1B1B29', fontSize: '14px' }}>
                          {field.label} {field.mandatory && <span style={{ color: '#E53E3E' }}>*</span>}
                        </label>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: isDisabled ? '#e2e8f0' : '#F3F6FB',
                          padding: '0 16px', borderRadius: '8px', border: '1px solid transparent',
                          height: type === 'text' ? 'auto' : '44px', boxSizing: 'border-box'
                        }}>
                          {type === 'check' ? (
                            <input
                              type="checkbox"
                              name={field.id}
                              checked={!!formData[field.id]}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              style={{ width: '18px', height: '18px', accentColor: '#604BE8' }}
                            />
                          ) : type === 'select' ? (
                            <select
                              name={field.id}
                              value={formData[field.id] || ''}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}
                            >
                              <option value="">Select {field.label}</option>
                              {(field.options || '').split('\n').map((opt, i) =>
                                opt.trim() && <option key={i} value={opt.trim()}>{opt.trim()}</option>
                              )}
                            </select>
                          ) : type === 'text' ? (
                            <textarea
                              name={field.id}
                              value={formData[field.id] || ''}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#1B1B29', fontWeight: '600', fontSize: '14px', minHeight: '80px', resize: 'vertical', padding: '12px 0' }}
                            />
                          ) : (
                            <input
                              type={type === 'float' || type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                              step={type === 'float' ? '0.001' : undefined}
                              name={field.id}
                              value={formData[field.id] ?? ''}
                              onChange={handleInputChange}
                              disabled={isDisabled}
                              placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                              style={{ flex: 1, height: '100%', border: 'none', background: 'transparent', outline: 'none', color: '#1B1B29', fontWeight: '600', fontSize: '14px' }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="form-section-card theme-green" style={{ marginTop: '20px' }}>
            <div className="section-header">
              <div className="section-icon"><FaSearch /></div>
              <h2>Existing Taxes ({filteredTaxes.length})</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', background: '#f8f9fa',
                padding: '10px 12px', borderRadius: '10px', border: '1px solid #e9ecef', marginBottom: '15px'
              }}>
                <FaSearch style={{ color: '#7f8c8d' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search taxes..."
                  style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', color: '#2c3e50' }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {companyOptions.length > 1 && (
                  <select
                    value={selectedViewCompany}
                    onChange={e => setSelectedViewCompany(e.target.value)}
                    style={{ padding: '10px 15px', borderRadius: '10px', border: '1px solid #3b82f6', outline: 'none', fontWeight: '600', cursor: 'pointer', background: '#eff6ff', color: '#1e3a8a' }}
                  >
                    <option value="">All Companies</option>
                    {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}

                {availableBranches.length > 0 && (
                  <select
                    value={selectedBranchFilter}
                    onChange={e => setSelectedBranchFilter(e.target.value)}
                    style={{ padding: '10px 15px', borderRadius: '10px', border: '1px solid #22c55e', outline: 'none', fontWeight: '600', cursor: 'pointer', background: '#f0fdf4', color: '#064e3b' }}
                  >
                    <option value="">All Branches</option>
                    {availableBranches.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                )}
              </div>

              <div className="premium-card">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Tax Name</th>
                      <th>Tax Type</th>
                      <th>Rate (%)</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th>Company</th>
                      <th>Branch</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTaxes.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}>
                          No Taxes Cataloged Yet.
                        </td>
                      </tr>
                    ) : (
                      filteredTaxes.map((tax) => (
                        <tr key={tax._id || tax.id}>
                          <td>
                            <div style={{ fontWeight: '600', color: '#2c3e50' }}>{tax.tax_name || '—'}</div>
                          </td>
                          <td>
                            <span style={{ background: '#f1f5f9', padding: '4px 10px', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', color: '#475569' }}>
                              {tax.tax_type || '—'}
                            </span>
                          </td>
                          <td style={{ fontWeight: '600', color: '#2c3e50' }}>
                            {tax.tax_rate !== undefined ? `${tax.tax_rate}%` : '—'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{
                              padding: '5px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold',
                              backgroundColor: tax.is_active ? '#dcfce7' : '#fee2e2',
                              color: tax.is_active ? '#16a34a' : '#dc2626'
                            }}>
                              {tax.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <span style={{ display: 'inline-block', background: '#eff6ff', color: '#3b82f6', borderRadius: '8px', padding: '4px 10px', fontSize: '0.8rem', fontWeight: '600' }}>
                              {tax.company_name || tax.company || '—'}
                            </span>
                          </td>
                          <td>
                            {tax.branch_name && tax.branch_name !== 'All Branches'
                              ? <span style={{ display: 'inline-block', background: '#ecfdf5', color: '#10b981', borderRadius: '8px', padding: '4px 10px', fontSize: '0.8rem', fontWeight: '600' }}>{tax.branch_name}</span>
                              : <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500' }}>All Branches</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {permissions.canWrite && (
                              <button
                                onClick={() => initiateEdit(tax)}
                                style={{ background: '#e3f2fd', color: '#2196f3', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', marginRight: '8px', transition: 'all 0.2s' }}
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                            )}
                            {permissions.canDelete && (
                              <button
                                onClick={() => { setDeleteId(tax._id || tax.id); setShowDeleteConfirm(true); }}
                                style={{ background: '#ffebee', color: '#f44336', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ── Assignment Modal ── */}
      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <span>Tax Master Assignments</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ marginTop: '20px' }}>
              {companyOptions.length > 0 && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#604BE8' }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {companyOptions.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff2f9', borderRadius: '12px', border: '1.5px solid #C3CDE4', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedCompanies.includes(comp)} 
                          onChange={() => toggleCompany(comp)} 
                          style={{ width: '18px', height: '18px', accentColor: '#604BE8' }} 
                        />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {(isGroupAdmin || isCompanyAdmin) && formBranches.length > 0 && (
                <div className="assignment-card">
                  <h4 style={{ color: '#06D6A0' }}><FaMapMarkerAlt /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                    {formBranches.map((br, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#ecfdf5', borderRadius: '12px', border: '1.5px solid #d1fae5', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedBranches.includes(br)} 
                          onChange={() => toggleBranch(br)} 
                          style={{ width: '18px', height: '18px', accentColor: '#06D6A0' }} 
                        />
                        {br}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleSubmit} disabled={loading || selectedCompanies.length === 0}>
                {loading ? "Adding..." : "Confirm & Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Error/Success Modal Overlays ── */}
      {error && !showAssignmentModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ width: '60px', height: '60px', background: '#fee2e2', color: '#e74c3c', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
              <FaExclamationTriangle />
            </div>
            <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Error</h3>
            <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{error}</p>
            <button onClick={() => setError('')} style={{ padding: '10px 20px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>
        </div>
      )}
      {successMessage && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10002, backdropFilter: 'blur(3px)' }}>
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '16px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ width: '60px', height: '60px', background: '#dcfce7', color: '#16a34a', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '30px', margin: '0 auto 15px' }}>
              <FaCheckCircle />
            </div>
            <h3 style={{ margin: '0 0 10px 0', color: '#1e293b' }}>Success</h3>
            <p style={{ color: '#64748b', margin: '0 0 20px 0' }}>{successMessage}</p>
            <button onClick={() => setSuccessMessage('')} style={{ padding: '10px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Awesome!</button>
          </div>
        </div>
      )}

      {/* ── Customize Modal ── */}
      {showCustomizeModal && (
        <CustomerCustomizationModal
          isOpen={showCustomizeModal}
          onClose={() => setShowCustomizeModal(false)}
          doctype="Tax Master"
          fields={doctypeFields}
          onFieldsUpdate={(newFields) => setDoctypeFields(newFields)}
        />
      )}

      {/* ── Delete Confirm Modal ── */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001, backdropFilter: 'blur(5px)' }}>
          <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '16px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', textAlign: 'center' }}>
            <div style={{ width: '70px', height: '70px', background: '#fee2e2', color: '#e74c3c', borderRadius: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px', margin: '0 auto 20px' }}>
                <FaTrash />
            </div>
            <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '22px', fontWeight: '700' }}>Confirm Deletion</h3>
            <p style={{ color: '#64748b', margin: '0 0 24px 0', fontSize: '15px' }}>Are you sure you want to delete this tax? All company & branch records will be removed permanently.</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{ flex: 1, padding: '14px', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer', background: '#f8fafc', fontWeight: '600', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={executeDelete}
                style={{ flex: 1, padding: '14px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', boxShadow: '0 4px 15px rgba(231, 76, 60, 0.3)' }}
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => fetchMetadata(baseUrl || '')}
        targetDocType="Tax Master"
      />
    </div>
  );
};

export default TaxMaster;
